import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  findCurrencySymbol,
  formatPrice,
} from '../../../services/utilityFunctions';
import { normalizeNumber } from '../../../services/numberUtilis';
import translate from '../../../commonComponents/translate';
import OrderProp from '../../shop/propTypes/orderProp';
import CurrencyProp from '../../profile/propTypes/currencyProp';

const renderTotals = (label, value) => (
  <div className="total-wrapper">
    <div className="total-wrapper-label">
      <p>{label}:</p>
    </div>
    <div className="total-wrapper-value">
      <p>{value}</p>
    </div>
  </div>
);

function OrderCreateSummary({
  t,
  orderDetailPrices,
  order,
  currencies,
  showVA,
  showCV,
  handleGoBack,
  enableSubmit,
  pageInformation,
  submitHandler,
}) {
  const currencySymbol = useMemo(
    () => findCurrencySymbol(order.currency, currencies),
    [order.currency, currencies],
  );

  const subtotal = useMemo(
    () => ({
      label: t('order.subtotal'),
      value: formatPrice(orderDetailPrices.subtotal, currencySymbol),
    }),
    [orderDetailPrices.subtotal, currencySymbol, t],
  );

  const userDiscount = useMemo(
    () => ({
      show: orderDetailPrices.userDiscount > 0,
      label: `${t('order_discount')} (${
        orderDetailPrices.userDiscountPercentage
      }%)`,
      value: `- ${formatPrice(orderDetailPrices.userDiscount, currencySymbol)}`,
    }),
    [
      orderDetailPrices.userDiscountPercentage,
      orderDetailPrices.userDiscount,
      currencySymbol,
      t,
    ],
  );

  const tax = useMemo(
    () => ({
      label: t('order.tax'),
      value: formatPrice(orderDetailPrices.tax, currencySymbol),
    }),
    [orderDetailPrices.tax, currencySymbol, t],
  );

  const subtotalExtraTax = useMemo(
    () => ({
      label: t('order.extraTax'),
      value: formatPrice(orderDetailPrices.subtotalExtraTax, currencySymbol),
      show: orderDetailPrices.subtotalExtraTax > 0,
    }),
    [orderDetailPrices.subtotalExtraTax, currencySymbol, t],
  );

  const shipping = useMemo(
    () => ({
      label: `${t('order.shipping')}${
        orderDetailPrices.shippingTotal > 0
          ? ` (${orderDetailPrices.shippingTax}%)`
          : ''
      }`,
      value: formatPrice(orderDetailPrices.shippingTotal, currencySymbol),
    }),
    [
      orderDetailPrices.shippingTotal,
      orderDetailPrices.shippingTax,
      currencySymbol,
      t,
    ],
  );

  const totalVA = useMemo(
    () => ({
      show: showVA,
      label: t('shop.totalVA'),
      value: orderDetailPrices?.totalVA
        ? normalizeNumber(orderDetailPrices.totalVA, 2)
        : 0,
    }),
    [showVA, orderDetailPrices?.totalVA, t],
  );

  const totalCV = useMemo(
    () => ({
      show: showCV,
      label: t('shop.totalCV'),
      value: orderDetailPrices?.totalCV
        ? normalizeNumber(orderDetailPrices.totalCV, 2)
        : 0,
    }),
    [showCV, orderDetailPrices?.totalCV, t],
  );

  const total = useMemo(
    () => ({
      label: t('order.total'),
      value: formatPrice(orderDetailPrices.total, currencySymbol),
    }),
    [orderDetailPrices.total, currencySymbol, t],
  );

  return (
    <div className="create-order-footer">
      <h3 className="subtitle">{t('shop.cart.estimation')}</h3>
      <div className="totals">
        {renderTotals(subtotal.label, subtotal.value)}
        {userDiscount.show &&
          renderTotals(userDiscount.label, userDiscount.value)}
        {renderTotals(tax.label, tax.value)}
        {subtotalExtraTax?.show &&
          renderTotals(subtotalExtraTax.label, subtotalExtraTax.value)}
        {renderTotals(shipping.label, shipping.value)}
        {totalVA.show && renderTotals(totalVA.label, totalVA.value)}
        {totalCV.show && renderTotals(totalCV.label, totalCV.value)}
        {renderTotals(total.label, total.value)}
      </div>
      <div className="create-order-buttons">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleGoBack}
        >
          {t('order_cancel')}
        </button>
        <button
          disabled={!enableSubmit}
          type="button"
          className={pageInformation.confirmClass}
          onClick={submitHandler}
        >
          {pageInformation.confirmButton}
        </button>
      </div>
    </div>
  );
}

OrderCreateSummary.propTypes = {
  t: PropTypes.func.isRequired,
  order: PropTypes.shape(OrderProp.propType).isRequired,
  orderDetailPrices: PropTypes.shape({
    subtotal: PropTypes.number,
    userDiscount: PropTypes.number,
    userDiscountPercentage: PropTypes.number,
    tax: PropTypes.number,
    shippingTotal: PropTypes.number,
    shippingTax: PropTypes.number,
    totalVA: PropTypes.number,
    totalCV: PropTypes.number,
    total: PropTypes.number,
    subtotalExtraTax: PropTypes.number,
  }).isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape(CurrencyProp.propType))
    .isRequired,
  showVA: PropTypes.bool,
  showCV: PropTypes.bool,
  handleGoBack: PropTypes.func.isRequired,
  enableSubmit: PropTypes.bool.isRequired,
  pageInformation: PropTypes.shape({
    confirmClass: PropTypes.string,
    confirmButton: PropTypes.string,
  }).isRequired,
  submitHandler: PropTypes.func.isRequired,
};

OrderCreateSummary.defaultProps = {
  showVA: false,
  showCV: false,
};

export default translate(OrderCreateSummary);
